import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, Eye, Download, Trash2, Edit, Lock } from 'lucide-react';
import { Button } from '../components/ui/button';

const GDPRPage = () => {
  return (
    <div className="min-h-screen bg-gray-950 pt-20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Back Link */}
        <Link to="/" className="inline-flex items-center text-gray-400 hover:text-white mb-8">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Home
        </Link>

        <h1 className="text-4xl font-bold text-white mb-4">GDPR Compliance</h1>
        <p className="text-gray-400 mb-8">Your Data Protection Rights Under the General Data Protection Regulation</p>
        <p className="text-gray-500 text-sm mb-8">Last updated: February 11, 2026</p>

        {/* Rights Overview Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-12">
          <div className="bg-gray-900/50 border border-gray-800 rounded-lg p-6">
            <div className="w-12 h-12 bg-amber-500/10 rounded-lg flex items-center justify-center mb-4">
              <Eye className="w-6 h-6 text-amber-400" />
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">Right to Access</h3>
            <p className="text-gray-400 text-sm">Request a copy of your personal data</p>
          </div>
          
          <div className="bg-gray-900/50 border border-gray-800 rounded-lg p-6">
            <div className="w-12 h-12 bg-emerald-500/10 rounded-lg flex items-center justify-center mb-4">
              <Edit className="w-6 h-6 text-emerald-400" />
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">Right to Rectification</h3>
            <p className="text-gray-400 text-sm">Correct inaccurate personal data</p>
          </div>
          
          <div className="bg-gray-900/50 border border-gray-800 rounded-lg p-6">
            <div className="w-12 h-12 bg-red-500/10 rounded-lg flex items-center justify-center mb-4">
              <Trash2 className="w-6 h-6 text-red-400" />
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">Right to Erasure</h3>
            <p className="text-gray-400 text-sm">Request deletion of your data</p>
          </div>
          
          <div className="bg-gray-900/50 border border-gray-800 rounded-lg p-6">
            <div className="w-12 h-12 bg-blue-500/10 rounded-lg flex items-center justify-center mb-4">
              <Download className="w-6 h-6 text-blue-400" />
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">Right to Portability</h3>
            <p className="text-gray-400 text-sm">Receive your data in a portable format</p>
          </div>
          
          <div className="bg-gray-900/50 border border-gray-800 rounded-lg p-6">
            <div className="w-12 h-12 bg-purple-500/10 rounded-lg flex items-center justify-center mb-4">
              <Shield className="w-6 h-6 text-purple-400" />
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">Right to Object</h3>
            <p className="text-gray-400 text-sm">Object to processing of your data</p>
          </div>
          
          <div className="bg-gray-900/50 border border-gray-800 rounded-lg p-6">
            <div className="w-12 h-12 bg-teal-500/10 rounded-lg flex items-center justify-center mb-4">
              <Lock className="w-6 h-6 text-teal-400" />
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">Right to Restrict</h3>
            <p className="text-gray-400 text-sm">Limit how we use your data</p>
          </div>
        </div>

        <div className="prose prose-invert max-w-none space-y-8">
          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">1. Our Commitment to GDPR</h2>
            <p className="text-gray-300 leading-relaxed">
              Life Decoder is committed to protecting and respecting your privacy in compliance with the 
              EU General Data Protection Regulation (GDPR). This page explains your rights under GDPR and 
              how you can exercise them.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">2. Data Controller</h2>
            <p className="text-gray-300 leading-relaxed">
              Life Decoder acts as the Data Controller for personal data collected through our website and 
              services. We determine the purposes and means of processing your personal data.
            </p>
            <div className="bg-gray-900/50 border border-gray-800 rounded-lg p-4 mt-4">
              <p className="text-gray-300">
                <strong className="text-white">Data Controller:</strong> Life Decoder Pte Ltd<br />
                <strong className="text-white">Address:</strong> No. H-8-2, Block H, Setiawalk Puchong, 47160 Selangor<br />
                <strong className="text-white">Email:</strong> team@digitaldivecoaching.com
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">3. Legal Basis for Processing</h2>
            <p className="text-gray-300 leading-relaxed mb-4">We process your personal data based on the following legal grounds:</p>
            <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
              <li><strong className="text-white">Consent:</strong> You have given clear consent for us to process your personal data for specific purposes</li>
              <li><strong className="text-white">Contract:</strong> Processing is necessary to fulfill our contractual obligations to you</li>
              <li><strong className="text-white">Legal Obligation:</strong> Processing is necessary to comply with legal requirements</li>
              <li><strong className="text-white">Legitimate Interests:</strong> Processing is necessary for our legitimate business interests</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">4. Your Rights Explained</h2>
            
            <div className="space-y-6">
              <div className="border-l-4 border-amber-500 pl-4">
                <h3 className="text-lg font-semibold text-white mb-2">Right of Access (Article 15)</h3>
                <p className="text-gray-300">
                  You have the right to obtain confirmation as to whether we are processing your personal data, 
                  and if so, access to that data along with information about how it is being processed.
                </p>
              </div>
              
              <div className="border-l-4 border-emerald-500 pl-4">
                <h3 className="text-lg font-semibold text-white mb-2">Right to Rectification (Article 16)</h3>
                <p className="text-gray-300">
                  You have the right to have inaccurate personal data corrected and incomplete data completed.
                </p>
              </div>
              
              <div className="border-l-4 border-red-500 pl-4">
                <h3 className="text-lg font-semibold text-white mb-2">Right to Erasure (Article 17)</h3>
                <p className="text-gray-300">
                  Also known as the "right to be forgotten," you may request deletion of your personal data 
                  under certain circumstances.
                </p>
              </div>
              
              <div className="border-l-4 border-blue-500 pl-4">
                <h3 className="text-lg font-semibold text-white mb-2">Right to Data Portability (Article 20)</h3>
                <p className="text-gray-300">
                  You have the right to receive your personal data in a structured, commonly used, 
                  machine-readable format and to transmit it to another controller.
                </p>
              </div>
              
              <div className="border-l-4 border-purple-500 pl-4">
                <h3 className="text-lg font-semibold text-white mb-2">Right to Object (Article 21)</h3>
                <p className="text-gray-300">
                  You have the right to object to processing of your personal data for direct marketing 
                  purposes or when processing is based on legitimate interests.
                </p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">5. Data Retention</h2>
            <p className="text-gray-300 leading-relaxed">
              We retain your personal data only for as long as necessary to fulfill the purposes for which 
              it was collected, including to satisfy legal, accounting, or reporting requirements. Different 
              retention periods apply to different types of data.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">6. International Transfers</h2>
            <p className="text-gray-300 leading-relaxed">
              If we transfer your personal data outside the European Economic Area (EEA), we ensure appropriate 
              safeguards are in place, such as Standard Contractual Clauses or transfers to countries with 
              adequate data protection laws.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">7. How to Exercise Your Rights</h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              To exercise any of your rights under GDPR, please contact our Data Protection Officer:
            </p>
            <div className="bg-gray-900/50 border border-gray-800 rounded-lg p-6">
              <p className="text-gray-300 mb-4">
                <strong className="text-white">Data Protection Officer</strong><br />
                Email: team@digitaldivecoaching.com<br />
                Response Time: Within 30 days
              </p>
              <Button className="bg-amber-600 hover:bg-amber-500">
                Submit a Data Request
              </Button>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">8. Right to Lodge a Complaint</h2>
            <p className="text-gray-300 leading-relaxed">
              If you believe we have not handled your personal data properly, you have the right to lodge a 
              complaint with a supervisory authority. In the EU, you can contact your local Data Protection 
              Authority.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">9. Updates to This Policy</h2>
            <p className="text-gray-300 leading-relaxed">
              We may update this GDPR compliance page from time to time. We will notify you of any significant 
              changes through our website or by email.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default GDPRPage;
